import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Activity, Zap, Brain } from "lucide-react";

/**
 * Quantitative Execution Core visualization.
 * Abstract, technology-first — no tickers, no instruments, no company names.
 * Shows: massive orders/sec, ML pipeline graph, cascading data streams,
 * signal throughput. Pure infrastructure vibe.
 */

const formatBigInt = (n) => n.toLocaleString("en-US");

// ML pipeline node layout (abstract, no labels visible — just topology)
const NODES = [
  { id: "in1", x: 18, y: 30, layer: 0 },
  { id: "in2", x: 18, y: 70, layer: 0 },
  { id: "in3", x: 18, y: 110, layer: 0 },
  { id: "h1", x: 95, y: 20, layer: 1 },
  { id: "h2", x: 95, y: 60, layer: 1 },
  { id: "h3", x: 95, y: 100, layer: 1 },
  { id: "h4", x: 95, y: 140, layer: 1 },
  { id: "m1", x: 175, y: 40, layer: 2 },
  { id: "m2", x: 175, y: 80, layer: 2 },
  { id: "m3", x: 175, y: 120, layer: 2 },
  { id: "o1", x: 255, y: 50, layer: 3 },
  { id: "o2", x: 255, y: 110, layer: 3 },
  { id: "exec", x: 330, y: 80, layer: 4 },
];

const EDGES = (() => {
  const e = [];
  const byLayer = NODES.reduce((acc, n) => {
    (acc[n.layer] = acc[n.layer] || []).push(n);
    return acc;
  }, {});
  for (let l = 0; l < 4; l++) {
    for (const a of byLayer[l]) {
      for (const b of byLayer[l + 1]) {
        e.push({ a, b });
      }
    }
  }
  return e;
})();

// Cascading data stream column
const StreamColumn = React.memo(({ delay = 0, speed = 6 }) => {
  // mix of hex + binary + decimal — feels like raw market microstructure
  const chars = useMemo(() => {
    const pool = "0123456789ABCDEF";
    const arr = [];
    for (let i = 0; i < 24; i++) {
      const len = 1 + Math.floor(Math.random() * 2);
      let s = "";
      for (let j = 0; j < len; j++) s += pool[Math.floor(Math.random() * pool.length)];
      arr.push(s);
    }
    return arr;
  }, []);
  return (
    <div className="qi-stream-col" style={{ animationDuration: `${speed}s`, animationDelay: `${delay}s` }}>
      {chars.map((c, i) => (
        <span key={i} className="qi-stream-char" style={{ opacity: 1 - i / chars.length }}>
          {c}
        </span>
      ))}
    </div>
  );
});

const HFTTerminal = () => {
  const [ordersPerSec, setOrdersPerSec] = useState(4287612);
  const [strategies, setStrategies] = useState(247);
  const [signals, setSignals] = useState(18423);
  const [dataPoints, setDataPoints] = useState(0); // billions
  const [throughput, setThroughput] = useState([]);
  const [tick, setTick] = useState(0);

  // Tick every 250ms — counters move FAST to convey scale
  useEffect(() => {
    const id = setInterval(() => {
      setOrdersPerSec((v) => v + Math.floor(Math.random() * 4200) + 800);
      setSignals((v) => v + Math.floor(Math.random() * 120) + 20);
      setDataPoints((v) => +(v + 0.13 + Math.random() * 0.18).toFixed(2));
      if (Math.random() > 0.85) setStrategies((s) => Math.max(240, Math.min(255, s + (Math.random() > 0.5 ? 1 : -1))));
      setThroughput((arr) => {
        const next = [...arr, 0.35 + Math.random() * 0.55];
        return next.slice(-40);
      });
      setTick((t) => (t + 1) % 1000);
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Build throughput polyline
  const sparkPath = useMemo(() => {
    if (throughput.length < 2) return "";
    const w = 360;
    const h = 38;
    return throughput
      .map((v, i) => {
        const x = (i / (throughput.length - 1)) * w;
        const y = h - v * h * 0.95;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }, [throughput]);

  // Random pulse pattern for nodes — deterministic per tick
  const nodePulses = useMemo(() => {
    return NODES.map((_, i) => ((tick + i * 7) % 13) / 13);
  }, [tick]);

  // Animated particles flowing along edges
  const particleEdges = useMemo(() => {
    const sample = [];
    for (let i = 0; i < 8; i++) {
      const e = EDGES[Math.floor(Math.random() * EDGES.length)];
      sample.push({ ...e, key: `${tick}-${i}`, dur: 1.2 + Math.random() * 1.4 });
    }
    return sample;
  }, [tick]);

  return (
    <div className="qi-terminal" data-testid="quant-infra-terminal" aria-hidden="true">
      {/* Header */}
      <div className="qi-header">
        <div className="qi-title">
          <Cpu size={14} className="qi-title-icon" />
          <span>QUANT EXECUTION CORE</span>
        </div>
        <div className="qi-status">
          <span className="qi-status-pill"><span className="qi-dot ok" />NEURAL-NET</span>
          <span className="qi-status-pill"><span className="qi-dot ok" />PIPELINE</span>
          <span className="qi-status-pill"><span className="qi-dot ok" />FPGA</span>
        </div>
      </div>

      {/* Metric grid */}
      <div className="qi-metrics">
        <div className="qi-metric">
          <div className="qi-metric-label">ORDERS / SEC</div>
          <motion.div
            key={ordersPerSec}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="qi-metric-big"
          >
            {formatBigInt(ordersPerSec)}
          </motion.div>
          <div className="qi-metric-trend pos">▲ AUTOMATED</div>
        </div>
        <div className="qi-metric">
          <div className="qi-metric-label">ACTIVE STRATEGIES</div>
          <div className="qi-metric-big">{strategies}</div>
          <div className="qi-metric-trend">QUANT MODELS</div>
        </div>
        <div className="qi-metric">
          <div className="qi-metric-label">SIGNALS / MIN</div>
          <motion.div key={signals} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="qi-metric-big">
            {formatBigInt(signals)}
          </motion.div>
          <div className="qi-metric-trend pos">REAL-TIME</div>
        </div>
      </div>

      {/* Neural pipeline graph */}
      <div className="qi-graph-wrap">
        <div className="qi-graph-label"><Brain size={11} /> ML INFERENCE PIPELINE</div>
        <svg viewBox="0 0 360 160" className="qi-graph" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="qi-edge" x1="0" x2="1">
              <stop offset="0%" stopColor="rgba(0,255,209,0.05)" />
              <stop offset="50%" stopColor="rgba(0,255,209,0.35)" />
              <stop offset="100%" stopColor="rgba(0,255,209,0.05)" />
            </linearGradient>
            <radialGradient id="qi-node-glow">
              <stop offset="0%" stopColor="rgba(0,255,209,0.6)" />
              <stop offset="100%" stopColor="rgba(0,255,209,0)" />
            </radialGradient>
          </defs>

          {/* Edges */}
          {EDGES.map((e, i) => (
            <line
              key={i}
              x1={e.a.x}
              y1={e.a.y}
              x2={e.b.x}
              y2={e.b.y}
              stroke="url(#qi-edge)"
              strokeWidth="0.6"
              opacity="0.7"
            />
          ))}

          {/* Flowing particles */}
          {particleEdges.map((p) => (
            <circle key={p.key} r="2" fill="#00FFD1">
              <animate attributeName="cx" from={p.a.x} to={p.b.x} dur={`${p.dur}s`} begin="0s" repeatCount="1" fill="freeze" />
              <animate attributeName="cy" from={p.a.y} to={p.b.y} dur={`${p.dur}s`} begin="0s" repeatCount="1" fill="freeze" />
              <animate attributeName="opacity" from="0" to="1" dur="0.2s" begin="0s" fill="freeze" />
            </circle>
          ))}

          {/* Nodes */}
          {NODES.map((n, i) => {
            const pulse = nodePulses[i];
            return (
              <g key={n.id}>
                <circle cx={n.x} cy={n.y} r={8 + pulse * 4} fill="url(#qi-node-glow)" opacity={0.4 + pulse * 0.5} />
                <circle cx={n.x} cy={n.y} r={3.2} fill="#0a1422" stroke="#00FFD1" strokeWidth="1" />
                <circle cx={n.x} cy={n.y} r={1.4} fill="#00FFD1" opacity={0.6 + pulse * 0.4} />
              </g>
            );
          })}

          {/* Final execution node — larger, brighter */}
          <circle cx={330} cy={80} r="14" fill="rgba(0,255,209,0.08)" stroke="rgba(0,255,209,0.4)" strokeWidth="0.6" />
          <text x={330} y={84} textAnchor="middle" fill="#00FFD1" fontSize="7.5" fontFamily="monospace" fontWeight="700">EXEC</text>
        </svg>
      </div>

      {/* Data streams + throughput */}
      <div className="qi-bottom">
        <div className="qi-streams">
          <StreamColumn delay={0} speed={4.2} />
          <StreamColumn delay={0.6} speed={5.8} />
          <StreamColumn delay={1.2} speed={3.6} />
          <StreamColumn delay={0.3} speed={6.5} />
          <StreamColumn delay={1.8} speed={4.8} />
          <StreamColumn delay={0.9} speed={5.2} />
          <StreamColumn delay={2.4} speed={3.9} />
        </div>
        <div className="qi-throughput">
          <div className="qi-throughput-head">
            <span><Activity size={10} /> THROUGHPUT</span>
            <span className="qi-throughput-num">{dataPoints.toFixed(2)} B EVENTS</span>
          </div>
          <svg viewBox="0 0 360 38" className="qi-spark" preserveAspectRatio="none">
            <polyline points={sparkPath} fill="none" stroke="#00FFD1" strokeWidth="1.2" />
            {sparkPath && <polyline points={`${sparkPath} 360,40 0,40`} fill="rgba(0,255,209,0.1)" stroke="none" />}
          </svg>
        </div>
      </div>

      {/* Scan line */}
      <div className="qi-scan-line" />
    </div>
  );
};

export default React.memo(HFTTerminal);
